import { getClassFunction } from '@helpers/functions';
import { researchSearch, researchSearchAutoComplete } from '@_api/research';
import {
  ResearchSearchApiData,
  ResearchSearchApiFilterData,
  ResearchSearchApiResponse,
  ResearchSearchAutoCompleteApiData,
} from '@_types/_api-response';
import autocomplete from 'autocompleter';
import getComponentMarkup from '@helpers/getComponentMarkup';
import { FilterDrawerModel, InsightsModel } from '@_types/organisms';
import FilterDrawer from '@organisms/filter-drawer/FilterDrawer';
import { researchSearchFilterDrawerData } from '@data/filterDrawer';
import Insights from '@organisms/insights/Insights';
import { insight2ColumnsSearchBase, insight2ColumnsSearchLoadMoreBase } from '@data/insights';
import { FiltersBarModel, LazyLoadMoreModel, PaginationWithNavStaticHtmlModel } from '@_types/molecules';
import LazyLoadMore from '@molecules/lazy-load-more/LazyLoadMore';
import { researchSearchLazyLoadMore, searchLazyLoadMore } from '@data/lazyLoadMore';
import FiltersBar from '@molecules/filters-bar/FiltersBar';
import { researchSearchFiltersBarData } from '@data/filtersBar';
import Divider from '@atoms/divider/Divider';
import RequireCss from '@helpers/RequireCss';
import { FilterDrawerClass } from '@helpers/FilterDrawer.class';
import { AccordionClass } from '@helpers/Accordion.class';
import { FilterCriterionClass } from '@helpers/FilterCriterion.class';
import { InsightsClass, ResearchSearchApiDataWithStyles } from '@helpers/Insights.class';
import { LazyLoadMoreClass } from '@helpers/LazyLoadMore.class';
import { FiltersBarClass } from '@helpers/FiltersBar.class';
import { searchPageSize } from '@data/researchSearchBar';
import { currentViewport } from '@helpers/breakpoint';
import { clearUrlQueries, pageKey, queryKey, searchFilterKey } from '@helpers/searchPage';
import {
  clearWorldwideScopeFromUrl,
  getSelectedWorldwideScope,
  parseWorldwideScopeFromUrl,
  resolveWorldwide,
  syncWorldwideScopeInUrl,
  worldwideScopeKey,
  WorldwideScopeValue,
  researchSearchAutocompleteMinLength,
  shouldRefetchResearchAutocompleteOnScopeChange,
  shouldReuseResearchAutocompleteCache,
} from '@helpers/worldwideScope';
import TagCommander, { SEARCH_QUERY_AGENT_ID } from '@helpers/TagCommanders.class';
import queryString from 'query-string';
import { PaginationWithNavStaticHtml } from '@molecules/pagination/PaginationWithNavButtonStaticHtml';
import { getCurrentTheme } from '@helpers/handleTheme';
import {
  hasCountryRedirectCopy,
  resolveCountryRedirectBodyTemplate,
  resolveCountryRedirectLanguageTemplate,
  setCountryRedirectHeading,
  setCountryRedirectBody,
  setCountryRedirectLanguage,
} from '@helpers/countryRedirectCopy';

const getClass = getClassFunction('kf-o-research-search-bar');

type RenderFilterDrawerData = {
  filters?: ResearchSearchApiFilterData[];
  heading?: string;
  subHeading?: string;
  filtersUpdateButtonLabel?: string;
  filtersCloseButtonLabel?: string;
};

class ResearchSearchBar {
  element: HTMLFormElement;
  searchId?: string;
  searchPageId?: string;
  fetching: boolean = false;
  searchButton: HTMLButtonElement | null;
  input: HTMLInputElement | null;
  optionsContainer: HTMLDivElement | null;
  drawerRendered: boolean = false;
  filterDrawerData?: RenderFilterDrawerData;
  initialContentRendered: boolean = false;
  contentElement: HTMLElement | null;
  timestamp: number;
  filterButton: HTMLElement | null;
  clearButton: HTMLElement | null;
  enableExtendedResultsView: boolean | undefined;
  currentPage = 1;
  isSectionSearch: boolean | undefined;
  sectionSearchCategories: string | undefined;
  searchPageUrl: string | undefined;
  usePagination: boolean | undefined;
  paginationPrevNavAriaLabel: string | undefined;
  paginationNextNavAriaLabel: string | undefined;
  paginationNodeAriaLabel: string | undefined;
  pageLanguage?: string | undefined;
  autocompleteEl: ReturnType<typeof autocomplete> | undefined;
  private autocompleteCacheQuery = '';
  private autocompleteCacheWorldwide: boolean | null = null;
  private autocompleteCacheResult: ResearchSearchAutoCompleteApiData[] = [];
  showWorldwideToggle: boolean;
  defaultWorldwide: boolean;
  private showCountryRedirectOverlay?: (href: string, lang?: string, language?: string) => boolean;

  constructor(element: HTMLFormElement) {
    this.element = element;
    this.searchId = this.element.dataset.searchId;
    this.searchPageId = this.element.dataset.searchPageId;
    this.searchButton = this.element.querySelector<HTMLButtonElement>(getClass('search-button'));
    this.input = this.element.querySelector<HTMLInputElement>('.kf-m-search-input__input');
    this.optionsContainer = this.element.querySelector<HTMLDivElement>('.kf-m-search-input__options');
    this.contentElement = document.querySelector<HTMLElement>('.kf-o-research-search-content');
    this.timestamp = new Date().getTime();
    this.filterButton = document.querySelector('.kf-o-research-search-bar__filter-button');
    this.clearButton = this.element?.querySelector<HTMLElement>('.kf-m-search-input__clear-button');
    this.enableExtendedResultsView = this.element.dataset.enableExtendedResultsView === 'true';

    this.usePagination = !!this.element.dataset.usePagination;
    this.paginationPrevNavAriaLabel = this.element.dataset.paginationPrevNavAriaLabel;
    this.paginationNextNavAriaLabel = this.element.dataset.paginationNextNavAriaLabel;
    this.paginationNodeAriaLabel = this.element.dataset.paginationNodeAriaLabel;

    const { isSectionSearch, sectionSearchCategories, searchPageUrl } = this.element.dataset;

    this.isSectionSearch = !!isSectionSearch;
    this.sectionSearchCategories = sectionSearchCategories;
    this.searchPageUrl = searchPageUrl;

    this.pageLanguage = this.element.dataset.searchPageLanguage;
    this.showWorldwideToggle = this.element.dataset.showWorldwideToggle === 'true';
    this.defaultWorldwide = this.element.dataset.defaultWorldwide === 'true';

    this.injectStyles();
    this.addEventToForm();
    this.initInputValue();
    this.initWorldwideScopeFromUrl();
    this.listenSearchEvent();
    this.listenFetchingDataEvent();
    this.handleAutocomplete();
    this.addEventToFilterButton();
    this.addEventToClearButton();
    this.addWorldwideScopeListeners();
    this.bindCountryRedirectOverlay();
  }

  getWorldwide(): boolean {
    const selected = this.element.querySelector<HTMLInputElement>('input[name="worldwide-scope"]:checked')?.value ?? null;
    return resolveWorldwide(this.showWorldwideToggle, this.defaultWorldwide, selected);
  }

  initWorldwideScopeFromUrl() {
    const url = new URL(window.location.href);

    if (!this.showWorldwideToggle) {
      this.clearWorldwideScopeFromUrlIfPresent(url);
      return;
    }

    const scope = parseWorldwideScopeFromUrl(url.searchParams.get(worldwideScopeKey));

    if (!scope) {
      return;
    }

    const radio = this.element.querySelector<HTMLInputElement>(`input[name="worldwide-scope"][value="${scope}"]`);

    if (radio) {
      radio.checked = true;
      this.syncWorldwideScopeRadioAriaChecked();
    }
  }

  private syncWorldwideScopeRadioAriaChecked() {
    this.element.querySelectorAll<HTMLInputElement>('input[name="worldwide-scope"]').forEach((radio) => {
      const checkMark = radio.nextElementSibling as HTMLElement | null;

      checkMark?.setAttribute('aria-checked', radio.checked ? 'true' : 'false');
    });
  }

  private clearWorldwideScopeFromUrlIfPresent(url: URL = new URL(window.location.href)) {
    if (!url.searchParams.has(worldwideScopeKey)) {
      return;
    }

    clearWorldwideScopeFromUrl(url);
    window.history.replaceState(null, '', url);
  }

  private syncWorldwideScopeInSearchUrl(url: URL) {
    if (!this.showWorldwideToggle) {
      clearWorldwideScopeFromUrl(url);
      return;
    }

    syncWorldwideScopeInUrl(url, this.getSelectedWorldwideScope(), this.defaultWorldwide);
  }

  getSelectedWorldwideScope(): WorldwideScopeValue {
    const selected = this.element.querySelector<HTMLInputElement>('input[name="worldwide-scope"]:checked')?.value ?? null;

    return getSelectedWorldwideScope(this.showWorldwideToggle, this.defaultWorldwide, selected);
  }

  addWorldwideScopeListeners() {
    this.element.querySelectorAll<HTMLInputElement>('input[name="worldwide-scope"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.dismissAutocompleteOptions();
        this.syncWorldwideScopeRadioAriaChecked();
        this.updateSearchUrl((url) => {
          url.searchParams.delete(pageKey);
          this.syncWorldwideScopeInSearchUrl(url);
        });
        this.refetchAutocompleteForCurrentScope();
        void this.search();
      });
    });
  }

  private dismissAutocompleteOptions() {
    if (this.optionsContainer?.parentElement) {
      this.optionsContainer.remove();
    }
  }

  private refetchAutocompleteForCurrentScope() {
    this.autocompleteCacheQuery = '';
    this.autocompleteCacheWorldwide = null;
    this.autocompleteCacheResult = [];

    if (
      document.activeElement === this.input &&
      shouldRefetchResearchAutocompleteOnScopeChange(this.input?.value.trim().length ?? 0)
    ) {
      this.autocompleteEl?.fetch();
    }
  }

  private updateSearchUrl(mutate: (url: URL) => void) {
    const url = new URL(window.location.href);
    mutate(url);
    window.history.replaceState(null, '', url);
  }

  bindCountryRedirectOverlay() {
    const dialog = document.querySelector<HTMLDialogElement>('.kf-o-country-redirect-overlay');
    if (!dialog) {
      return;
    }

    if (!dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }

    if (dialog.parentElement !== document.body) {
      document.body.appendChild(dialog);
    }

    const bodyOverflowCssPropertyName = '--body-overflow-y-by-site-preference';
    const headingEl = dialog.querySelector<HTMLElement>('.kf-o-country-redirect-overlay__heading');
    const bodyEl = dialog.querySelector<HTMLElement>('.kf-o-country-redirect-overlay__body');
    const languageEl = dialog.querySelector<HTMLElement>('.kf-o-country-redirect-overlay__language');
    const continueLink = dialog.querySelector<HTMLAnchorElement>('.kf-o-country-redirect-overlay__continue');
    const closeButton = dialog.querySelector<HTMLButtonElement>('.kf-o-country-redirect-overlay__close');
    const dialogContent = dialog.querySelector<HTMLElement>('.kf-o-country-redirect-overlay__content');
    const headingTemplate = dialog.dataset.headingTemplate ?? '';
    const bodyTemplate = dialog.dataset.bodyTemplate ?? '';
    const languageTemplate = dialog.dataset.languageTemplate ?? '';

    const setContinueLinkHref = (href: string) => {
      if (!continueLink) {
        return;
      }

      try {
        const destination = new URL(href, window.location.origin);

        if (destination.protocol !== 'http:' && destination.protocol !== 'https:') {
          continueLink.removeAttribute('href');
          continueLink.classList.add('disabled');
          return;
        }

        continueLink.href = destination.toString();
        continueLink.classList.remove('disabled');
      } catch {
        continueLink.removeAttribute('href');
        continueLink.classList.add('disabled');
      }
    };

    const clearContinueLink = () => {
      continueLink?.removeAttribute('href');
      continueLink?.classList.add('disabled');
    };

    const lockBodyScroll = () => {
      document.body.style.setProperty(bodyOverflowCssPropertyName, 'hidden');
    };

    const unlockBodyScroll = () => {
      document.body.style.removeProperty(bodyOverflowCssPropertyName);
    };

    const showCountryRedirectOverlay = (href: string, lang?: string, language?: string) => {
      setContinueLinkHref(href);

      const context = {
        url: href,
        lang,
        language,
      };

      if (headingEl && headingTemplate.trim()) {
        setCountryRedirectHeading(headingEl, headingTemplate, context);
      } else if (headingEl) {
        headingEl.textContent = '';
      }

      const hasCmsCopy = hasCountryRedirectCopy(headingTemplate, bodyTemplate, languageTemplate);
      const bodyMessage = resolveCountryRedirectBodyTemplate(languageTemplate, bodyTemplate) || (hasCmsCopy ? '' : '{url}');
      const resolvedLanguage = resolveCountryRedirectLanguageTemplate(languageTemplate, bodyTemplate);

      if (bodyEl && bodyMessage.trim()) {
        setCountryRedirectBody(bodyEl, bodyMessage, context);
      } else if (bodyEl) {
        bodyEl.replaceChildren();
      }

      if (languageEl) {
        if (resolvedLanguage) {
          setCountryRedirectLanguage(languageEl, resolvedLanguage, context);
          languageEl.hidden = false;
        } else {
          languageEl.replaceChildren();
          languageEl.hidden = true;
        }
      }

      this.updateCountryRedirectDialogAccessibility(
        dialog,
        headingEl,
        bodyEl,
        languageEl,
        headingTemplate,
        bodyMessage,
        resolvedLanguage
      );

      lockBodyScroll();
      this.dismissAutocompleteOptions();
      dialog.showModal();
      dialog.focus({ preventScroll: true });

      return true;
    };

    document.addEventListener(
      'click',
      (event) => {
        const target = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[data-is-other-site="true"]');
        if (!target?.href) {
          return;
        }

        event.preventDefault();
        showCountryRedirectOverlay(target.href, target.dataset.lang, target.dataset.language);
      },
      true
    );

    const closeDialog = () => {
      if (!dialog.open) {
        return;
      }

      dialog.close();
      clearContinueLink();
      unlockBodyScroll();
    };

    closeButton?.addEventListener('click', closeDialog);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('close', unlockBodyScroll);
    dialog.addEventListener('pointerdown', (event) => {
      if (!dialogContent || dialogContent.contains(event.target as Node)) {
        return;
      }

      event.preventDefault();
      closeDialog();
    });

    this.showCountryRedirectOverlay = showCountryRedirectOverlay;
  }

  private updateCountryRedirectDialogAccessibility(
    dialog: HTMLDialogElement,
    headingEl: HTMLElement | null,
    bodyEl: HTMLElement | null,
    languageEl: HTMLElement | null,
    headingTemplate: string,
    bodyMessage: string,
    resolvedLanguage?: string
  ) {
    const headingId = 'country-redirect-overlay-heading';
    const bodyId = 'country-redirect-overlay-body';
    const languageId = 'country-redirect-overlay-language';

    if (headingTemplate.trim() && headingEl?.textContent?.trim()) {
      dialog.setAttribute('aria-labelledby', headingId);
      dialog.removeAttribute('aria-label');
      return;
    }

    if (bodyMessage.trim() && bodyEl?.textContent?.trim()) {
      dialog.setAttribute('aria-labelledby', bodyId);
      dialog.removeAttribute('aria-label');
      return;
    }

    if (resolvedLanguage && languageEl?.textContent?.trim()) {
      dialog.setAttribute('aria-labelledby', languageId);
      dialog.removeAttribute('aria-label');
      return;
    }

    dialog.removeAttribute('aria-labelledby');
    dialog.setAttribute('aria-label', 'Country redirect');
  }

  listenFetchingDataEvent() {
    window.addEventListener(`${this.searchId}-fetching`, () => {
      this.fetching = true;
    });

    window.addEventListener(`${this.searchId}-done`, () => {
      this.fetching = false;
      this.searchButton?.removeAttribute('disabled');
    });
  }

  addEventToFilterButton() {
    if (!this.filterButton) return;
    const filterDrawerId = this.filterButton.dataset.filterDrawerId;

    this.filterButton?.addEventListener('click', () => {
      if (!filterDrawerId) return;

      if (!this.drawerRendered && this.filterDrawerData) {
        this.renderFilterDrawer(this.filterDrawerData);

        this.initFilterBlocks();
      }

      const filterDrawer = document.querySelector<HTMLElement>(`.kf-o-filter-drawer[data-id="${filterDrawerId}"]`);

      setTimeout(() => {
        filterDrawer?.dispatchEvent(new CustomEvent('open-drawer'));
      }, 100);
    });
  }

  initContentBlocks() {
    if (!this.contentElement) return;
    const insightsBlocks = this.contentElement.querySelectorAll<HTMLElement>('.kf-o-insights');

    insightsBlocks.forEach((block) => new InsightsClass(block));

    const lazyLoadMoreBlock = this.contentElement.querySelector<HTMLElement>('.kf-m-lazy-load-more');

    if (lazyLoadMoreBlock) {
      // Instantiation is for side effects only; suppress unused warning
      new LazyLoadMoreClass(lazyLoadMoreBlock).init();
    }
  }

  initFiltersBar() {
    if (!this.contentElement) return;

    const filtersBar = this.contentElement.querySelector<HTMLElement>('.kf-m-filters-bar');

    if (!filtersBar) return;

    // Instantiation is for side effects only; suppress unused warning
    new FiltersBarClass(filtersBar).init();
  }

  initFilterBlocks() {
    if (!this.filterButton) return;
    const filterDrawerId = this.filterButton.dataset.filterDrawerId;

    const filterDrawer = document.querySelector<HTMLElement>(`.kf-o-filter-drawer[data-id="${filterDrawerId}"]`);

    if (!filterDrawer) return;

    // Instantiation is for side effects only; suppress unused warning
    new FilterDrawerClass(filterDrawer).init();

    const filterAccordion = filterDrawer.querySelector<HTMLElement>('.kf-o-accordion');

    if (filterAccordion) {
      // Instantiation is for side effects only; suppress unused warning
      new AccordionClass(filterAccordion).init();
    }

    const filterCriteria = filterDrawer.querySelectorAll<HTMLElement>('.kf-m-filter-criterion');

    filterCriteria.forEach((filter) => new FilterCriterionClass(filter));
  }

  addEventToForm() {
    this.element.addEventListener('submit', (event) => {
      event.preventDefault();

      if (this.isSectionSearch) {
        this.handleSubmitIfElementIsSectionSearch();

        return;
      }

      if (this.fetching) return;

      this.input?.blur();

      const formData = new FormData(this.element);
      const query = formData.get(queryKey);

      this.updateSearchUrl((url) => {
        if (query) {
          url.searchParams.set(queryKey, query.toString());
        } else {
          url.searchParams.delete(queryKey);
        }

        const categories = url.searchParams.get(searchFilterKey);

        clearUrlQueries(url);

        if (categories) {
          url.searchParams.set(searchFilterKey, categories);
        }

        this.syncWorldwideScopeInSearchUrl(url);
      });

      void this.search();
    });
  }

  handleSubmitIfElementIsSectionSearch() {
    if (!this.searchPageUrl) throw new Error('data-search-page-url attribute must be set');
    const formData = new FormData(this.element);
    const query = formData.get(queryKey);
    const categories = this.sectionSearchCategories ?? null;

    let url: URL;

    try {
      url = new URL(this.searchPageUrl);
    } catch {
      url = new URL(this.searchPageUrl, window.location.origin);
    }
    if (query) url.searchParams.set(queryKey, query.toString());
    if (categories) url.searchParams.set(searchFilterKey, categories);
    this.syncWorldwideScopeInSearchUrl(url);

    window.location.href = url.toString();
  }

  initInputValue() {
    if (!this.input) return;

    const url = new URL(window.location.href);

    const query = url.searchParams.get(queryKey);

    if (!query) return;

    this.input.value = query;
  }

  listenSearchEvent() {
    if (!this.searchId) return;

    window.addEventListener(this.searchId, async () => {
      await this.search();
    });
  }

  addClickEventHandlerToPaginationNodes() {
    const paginationContainer = this.getCurrentPagination();

    if (!paginationContainer) return;

    const paginationNodeSelector = '[data-is-pagination-node]';
    const navigationNodes = paginationContainer.querySelectorAll(paginationNodeSelector);

    if (!navigationNodes || !navigationNodes.length) return;
    navigationNodes.forEach((node) => {
      node.addEventListener('click', (e) => {
        e.preventDefault();
        const page = (e.currentTarget as HTMLElement).dataset.pageNumber;

        if (!page) return;
        const url = new URL(window.location.href);

        if (page === '1') {
          url.searchParams.delete(pageKey);
        } else {
          url.searchParams.set(pageKey, page);
        }

        window.history.replaceState(null, '', url);
        void this.search({ scrollToResults: true });
      });
    });
  }

  renderFiltersBar(filters: string | null, selectedFiltersLabel?: string, showingTotalResultsLabel?: string) {
    if (!this.contentElement) return;

    const markup = getComponentMarkup<FiltersBarModel>(FiltersBar, {
      ...researchSearchFiltersBarData,
      selectedFilters: selectedFiltersLabel && filters ? selectedFiltersLabel : '',
      totalResults: showingTotalResultsLabel,
      disabled: !this.filterButton,
    });

    this.contentElement.insertAdjacentHTML('afterbegin', markup);

    this.initFiltersBar();
  }

  async search(options?: { scrollToResults?: boolean }) {
    const url = new URL(window.location.href);
    const query = url.searchParams.get(queryKey);
    const filters = url.searchParams.get(searchFilterKey);
    const page = url.searchParams.get(pageKey) || '1';
    const { skip, take } = this.calculateSearchApiParameters(parseInt(page));

    window.dispatchEvent(new CustomEvent(`${this.searchId}-fetching`));

    const getData = async () =>
      await researchSearch({
        data: {
          skip,
          take,
          searchPageId: this.searchPageId,
          languageCode: this.pageLanguage,
          worldwide: this.getWorldwide(),
          ...(query ? { query } : null),
          ...(this.filterButton && filters ? { categoryIds: filters } : null),
        },
      });

    const response = await getData();
    let filters_selected = '';
    const category = queryString.parse(window.location.search, { arrayFormat: 'comma' })[searchFilterKey];
    const evaluateCategory = () => (Array.isArray(category) ? category : [category]);
    const valuesCategory = evaluateCategory();

    const evaluateFilter = () => {
      if (valuesCategory instanceof Array) {
        const datasCategory = this.flatFilters(response.data.filters);

        filters_selected = valuesCategory
          .map((value) => {
            const filter = datasCategory.find((data) => data.value?.toString() === value);

            return filter?.text;
          })
          .join(', ');
      }
    };

    evaluateFilter();

    const sendTcData = () => {
      TagCommander.getInstance().getAgent(SEARCH_QUERY_AGENT_ID)?.sendData(window, {
        search_keywords: query,
        filters_selected: filters_selected,
        page_path: window.document.location,
        search_scope: this.getSelectedWorldwideScope(),
        worldwide: this.getWorldwide(),
      });
    };

    sendTcData();

    window.dispatchEvent(new CustomEvent(`${this.searchId}-done`));

    if (!response?.data) return;

    const getFilterDrawerData = () => {
      this.filterDrawerData = {
        filters: response.data.filters,
        heading: response.data.localization?.filtersHeading,
        subHeading: response.data.localization?.filtersSubHeading,
        filtersUpdateButtonLabel: response.data.localization?.filtersUpdateButtonLabel,
        filtersCloseButtonLabel: response.data.localization?.filtersCloseButtonLabel,
      };
    };

    getFilterDrawerData();

    const itemType = this.element.dataset.itemType;
    const total = response?.data?.meta?.total ?? 0;

    const getItemCount = () => response?.data?.items?.length ?? 0;
    const count = getItemCount();
    const offset = response?.data?.meta?.offset ?? 0;

    const getPaginationData = () => (this.usePagination ? this.createPaginationDataFromSearchApiResponse(response) : undefined);
    const paginationData = getPaginationData();

    const initialRenderSectionAndFilterBarAndData = () => {
      if (this.initialContentRendered) return;
      this.renderSections(
        response.data.items?.length,
        response.data.localization?.showMoreButtonLabel,
        response.data?.localization?.showingXOfXResultsLabel,
        paginationData
      );
      this.renderFiltersBar(filters, response.data?.localization?.selectedFiltersLabel, response.data?.localization?.showingTotalResultsLabel);
      setTimeout(() => {
        this.renderData(count, total, offset, response.data.items, parseInt(page), itemType);
      }, 200);
    };

    initialRenderSectionAndFilterBarAndData();

    const subsequentRender = () => {
      if (!this.initialContentRendered) return;

      const hasResultSections = !!this.contentElement?.querySelector('.kf-o-research-search-content__first-results');

      if (count > 0 && !hasResultSections) {
        this.renderSections(
          response.data.items?.length,
          response.data.localization?.showMoreButtonLabel,
          response.data?.localization?.showingXOfXResultsLabel,
          paginationData
        );
      }

      this.renderData(count, total, offset, response.data.items, parseInt(page), itemType);
      if (this.usePagination) {
        this.refreshPagination(paginationData);
        if (options?.scrollToResults) {
          this.contentElement?.scrollIntoView();
        }
      }
    };

    subsequentRender();

    const renderStatisticTexts = () =>
      this.renderStatisticTexts(
        filters,
        page,
        this.calculateSearchApiParameters(parseInt(page) + 1).take,
        response.data?.localization?.selectedFiltersLabel,
        response?.data?.localization?.showingTotalResultsLabel,
        response?.data?.localization?.showingXOfXResultsLabel,
        total,
        count,
        this.calculateSearchApiParameters(parseInt(page) + 1).skip
      );

    renderStatisticTexts();

    this.renderFiltersBarItems(response.data.filters);

    if (!this.initialContentRendered) {
      this.initialContentRendered = true;
    }
  }

  toggleClearButtonVisible(isVisible: boolean) {
    if (!this.clearButton) return;

    if (isVisible) {
      this.clearButton.classList.add('active');

      return;
    }

    this.clearButton.classList.remove('active');
  }

  addEventToClearButton() {
    this.clearButton?.addEventListener('click', this.onClearButtonPressed.bind(this));

    this.clearButton?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.onClearButtonPressed();
      }
    });
  }

  onClearButtonPressed() {
    if (!this.input) return;

    this.input.value = '';
    this.toggleClearButtonVisible(false);
  }

  injectStyles() {
    const insightsStyle = getComponentMarkup(RequireCss, { path: `b-insights.css?v=${this.timestamp}` });
    const insightItemStyle = getComponentMarkup(RequireCss, { path: `b-insight-item.css?v=${this.timestamp}` });
    const accordionStyle = getComponentMarkup(RequireCss, { path: `b-accordion.css?v=${this.timestamp}` });

    document.head.insertAdjacentHTML('beforeend', insightsStyle);
    document.head.insertAdjacentHTML('beforeend', insightItemStyle);
    document.head.insertAdjacentHTML('beforeend', accordionStyle);
  }

  renderSections(
    itemLength?: number,
    showMoreButtonLabel?: string,
    showingXOfXResultsLabel?: string,
    paginationData?: PaginationWithNavStaticHtmlModel
  ) {
    const firstSectionMarkup = getComponentMarkup<InsightsModel>(Insights, {
      ...insight2ColumnsSearchBase,
      dataSet: {
        'data-lazy-load-more-source-id': this.enableExtendedResultsView ? undefined : 'research-search-load-more',
      },
    });

    let secondSectionMarkup = '';
    let trippedDottedMarkup = '';
    let lazyLoadMarkup = '';
    let paginationMarkup = '';

    if (this.enableExtendedResultsView || this.usePagination) {
      secondSectionMarkup = getComponentMarkup<InsightsModel>(Insights, {
        ...insight2ColumnsSearchLoadMoreBase,
        dataSet: {
          ...insight2ColumnsSearchLoadMoreBase.dataSet,
        },
      });

      trippedDottedMarkup = getComponentMarkup(Divider, { styleModifier: ['tripped-dotted'] });
    }

    const isResearch = document.body.classList.contains('kf-research');
    const data = isResearch ? researchSearchLazyLoadMore : searchLazyLoadMore;
    const { pageSizeDesktop, pageSizeTablet, pageSizeMobile } = this.getPageSize();

    if (!this.usePagination)
      lazyLoadMarkup = getComponentMarkup<LazyLoadMoreModel>(LazyLoadMore, {
        ...data,
        pageSize: {
          desktop: pageSizeDesktop,
          tablet: pageSizeTablet,
          mobile: pageSizeMobile,
        },
        loadMoreButton: { ...data.loadMoreButton, text: showMoreButtonLabel },
        label: showingXOfXResultsLabel,
        searchPageId: this.searchPageId,
      });

    if (this.usePagination && paginationData) paginationMarkup = this.createPaginationHtml(paginationData);

    if (!this.contentElement) return;

    const remainingResultsMarkup =
      secondSectionMarkup &&
      `<div class="kf-o-research-search-content__remaining-results">
          <div class="kf-container">
            ${trippedDottedMarkup}
          </div>
          ${secondSectionMarkup}
        </div>`;

    if (itemLength && itemLength > 0) {
      const sectionsMarkup = `
        <div class="kf-o-research-search-content__first-results">
          ${firstSectionMarkup}
        </div>
        ${remainingResultsMarkup}
        ${lazyLoadMarkup}
        <div data-is-pagination-container="">
          ${paginationMarkup}
        </div>
      `;
      const filtersBar = this.contentElement.querySelector('.kf-m-filters-bar');

      if (filtersBar) {
        filtersBar.insertAdjacentHTML('afterend', sectionsMarkup);
      } else {
        this.contentElement.insertAdjacentHTML('afterbegin', sectionsMarkup);
      }
    }

    this.initContentBlocks();

    if (this.usePagination) this.addClickEventHandlerToPaginationNodes();
  }

  renderData(count: number, total: number, offset: number, items: ResearchSearchApiData[], page: number, itemType?: string, query?: string | null) {
    if (!this.contentElement) return;
    const firstSection = this.contentElement.querySelector<HTMLElement>('.kf-o-research-search-content__first-results');
    const secondSection = this.contentElement.querySelector<HTMLElement>('.kf-o-research-search-content__remaining-results');
    const noContentSection = this.contentElement.querySelector<HTMLElement>('.kf-o-research-search-content__no-result');
    const lazyLoadMoreSection = this.contentElement.querySelector<HTMLElement>('.kf-m-lazy-load-more');

    const transformDom = () => {
      noContentSection?.setAttribute('data-show', 'true');
      firstSection?.removeAttribute('data-show');
      secondSection?.removeAttribute('data-show');
      lazyLoadMoreSection?.removeAttribute('data-show');
      this.refreshPagination();
    };

    if (!items?.length) {
      transformDom();

      return;
    }

    noContentSection?.removeAttribute('data-show');
    lazyLoadMoreSection?.setAttribute('data-show', 'true');

    const numberOfFirstResultItems = +(this.contentElement?.dataset.numberOfFirstResultItems || '8');

    const handleFirstSection = () => {
      let firstSectionItemsWithStyleModifier: ResearchSearchApiDataWithStyles[] = [];
      let firstSectionItems: ResearchSearchApiData[] = [];

      if (!this.usePagination || (this.usePagination && page === 1))
        firstSectionItems = !this.enableExtendedResultsView ? items : items.slice(0, numberOfFirstResultItems);

      firstSectionItemsWithStyleModifier = firstSectionItems.map((item): ResearchSearchApiDataWithStyles => {
        return {
          ...this.appendQueryToUrl(item, query),
          styleModifier: !item.imageUrl
            ? ['background-highlighted', 'horizontal', 'spacing-sm', 'no-animation']
            : ['horizontal', 'spacing-sm', 'no-animation'],
        };
      });

      this.dispatchEventRenderData(firstSectionItemsWithStyleModifier, firstSection, itemType);
    };

    handleFirstSection();

    const handleSecondSection = () => {
      let secondSectionItemsWithStyleModifier: ResearchSearchApiDataWithStyles[] = [];
      let secondSectionItems: ResearchSearchApiData[] = [];

      if (this.enableExtendedResultsView) secondSectionItems = items.slice(numberOfFirstResultItems, items?.length);
      if (this.usePagination && page !== 1) {
        secondSectionItems = items;
        secondSection?.classList.add('hide-divider');
      } else {
        secondSection?.classList.remove('hide-divider');
      }
      secondSectionItemsWithStyleModifier = secondSectionItems.map(
        (item): ResearchSearchApiDataWithStyles => ({
          ...this.appendQueryToUrl(item, query),
          styleModifier:
            this.usePagination && !this.enableExtendedResultsView && !item.imageUrl
              ? ['background-highlighted', 'horizontal', 'spacing-sm', 'no-animation']
              : ['horizontal', 'spacing-sm', 'no-animation'],
          description: item.description,
          imageUrl: !this.usePagination ? '' : this.enableExtendedResultsView ? '' : item.imageUrl,
        })
      );

      this.dispatchEventRenderData(secondSectionItemsWithStyleModifier, secondSection, itemType);
    };

    handleSecondSection();

    const loadMore = document.querySelector<HTMLElement>('.kf-o-research-search-content .kf-m-lazy-load-more');

    const hasMoreData = count + offset < total;

    if (loadMore) {
      if (hasMoreData) {
        loadMore.dataset.show = 'true';
      } else {
        loadMore.removeAttribute('data-show');
      }
    }
  }

  renderSelectedFilters(filters?: string | null, selectedFiltersLabel?: string | null) {
    const filterBar = this.contentElement?.querySelector<HTMLElement>('.kf-m-filters-bar');

    if (!filterBar) return;

    const selectedFilters = this.contentElement?.querySelector<HTMLElement>(
      `.kf-m-filters-bar[data-search-id=${this.searchId}] .kf-m-filters-bar__selected-filter .kf-m-filters-bar__text-content`
    );

    if (!filters) {
      selectedFilters?.remove();

      return;
    }

    if (!selectedFilters) {
      filterBar.insertAdjacentHTML(
        'afterbegin',
        `
          <div class="kf-m-filters-bar__selected-filter">
            <p class="kf-m-filters-bar__text-content">${selectedFiltersLabel}</p>
          </div>
          `
      );

      return;
    }

    selectedFilters.textContent = selectedFiltersLabel || '';
  }

  renderStatisticTexts(
    filters: string | null,
    currentPage: string,
    take: number,
    selectedFiltersLabel?: string,
    totalResultText?: string,
    loadMoreLabelText?: string,
    total?: number,
    count?: number,
    skip?: number
  ) {
    const totalResult = this.contentElement?.querySelector<HTMLElement>(
      `.kf-m-filters-bar[data-search-id=${this.searchId}] .kf-m-filters-bar__result .kf-m-filters-bar__text-content`
    );

    if (totalResult && totalResultText) {
      totalResult.textContent = totalResultText;
    }

    this.renderSelectedFilters(filters, selectedFiltersLabel);

    const loadMore = document.querySelector<HTMLElement>('.kf-o-research-search-content .kf-m-lazy-load-more');

    if (!loadMore) return;

    loadMore.dispatchEvent(
      new CustomEvent('lazy-load-statistic-data', {
        detail: {
          count,
          showingXOfXResultsLabel: loadMoreLabelText,
          currentPage,
          total,
          filterQueryName: this.filterButton ? 'categoryIds' : undefined,
          source: 'external',
          withImage: !this.enableExtendedResultsView,
          take,
          skip,
        },
      })
    );
  }

  handleAutocomplete() {
    if (!this.input) return;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    this.clearButton?.addEventListener('focus', () => {
      timeout = setTimeout(() => {
        this.autocompleteEl?.fetch();
      }, 300);
    });

    this.clearButton?.addEventListener('blur', () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    });

    const focusOutHandler = (e: Event) => {
      const fe = e as FocusEvent;
      const relatedTarget = fe.relatedTarget as HTMLElement | null;
      const isItem = relatedTarget?.classList.contains('kf-m-search-input__autocomplete-item');

      if (isItem) return;

      this.dismissAutocompleteOptions();
    };

    const inputWrapper = this.input.closest('.kf-m-search-input__input-wrapper');

    inputWrapper?.addEventListener('focusout', focusOutHandler);

    document.addEventListener('pointerdown', (event) => {
      if (document.querySelector<HTMLDialogElement>('.kf-o-country-redirect-overlay')?.open) {
        return;
      }

      const target = event.target as Node;

      if (!inputWrapper || !this.optionsContainer?.parentElement) {
        return;
      }

      if (inputWrapper.contains(target)) {
        return;
      }

      this.dismissAutocompleteOptions();
    });

    this.autocompleteEl = autocomplete<ResearchSearchAutoCompleteApiData>({
      input: this.input,
      fetch: async (text: string, update: (items: ResearchSearchAutoCompleteApiData[]) => void) => {
        if (text.length) {
          this.toggleClearButtonVisible(true);
        }

        const worldwide = this.getWorldwide();

        if (shouldReuseResearchAutocompleteCache(this.autocompleteCacheQuery, this.autocompleteCacheWorldwide, text, worldwide)) {
          return update(this.autocompleteCacheResult);
        }

        const response = await researchSearchAutoComplete({
          data: {
            query: text,
            searchPageId: this.searchPageId,
            languageCode: this.pageLanguage,
            worldwide,
          },
        });

        this.autocompleteCacheQuery = text;
        this.autocompleteCacheWorldwide = worldwide;
        this.autocompleteCacheResult = response?.data?.data ?? [];

        if (this.autocompleteCacheResult.length) {
          update(this.autocompleteCacheResult);
        } else {
          update([]);
        }
      },
      minLength: researchSearchAutocompleteMinLength,
      debounceWaitMs: 300,
      preventSubmit: 2,
      className: 'kf-m-search-input__autocomplete',
      container: this.optionsContainer ?? undefined,
      onSelect: (item: ResearchSearchAutoCompleteApiData) => {
        if (!item.url) {
          return;
        }

        if (
          item.isOtherSite &&
          this.showCountryRedirectOverlay?.(item.url, item.languageCode, item.languageName)
        ) {
          return;
        }

        window.location.href = item.url;
      },
      disableAutoSelect: true,
      showOnFocus: true,
      click: (e) => e.fetch(),
      render: (item: ResearchSearchAutoCompleteApiData) => {
        const div = document.createElement('div');
        const el = document.createElement(item.url ? 'a' : 'div');

        el.className = 'kf-m-search-input__autocomplete-item';

        if (item.url) {
          const anchor = el as HTMLAnchorElement;

          anchor.href = item.url;

          if (item.isOtherSite) {
            anchor.dataset.isOtherSite = 'true';
          }

          if (item.languageCode) {
            anchor.dataset.lang = item.languageCode;
          }

          if (item.languageName) {
            anchor.dataset.language = item.languageName;
          }
        }

        const label = document.createElement('div');

        label.className = 'kf-m-search-input__autocomplete-item__label';
        label.textContent = item.label;
        el.append(label);

        if (item.description) {
          const description = document.createElement('span');

          description.className = 'kf-m-search-input__autocomplete-item__description';
          description.textContent = item.description;
          el.append(description);
        }

        div.append(el);

        return div;
      },
      keyup: () => {
        if (!this.input) return;

        if (this.input.value) {
          this.toggleClearButtonVisible(true);
        } else {
          this.toggleClearButtonVisible(false);
        }
      },
    });
  }

  renderFilterDrawer(values: RenderFilterDrawerData) {
    const data = this.transformFilterDrawerData(values);
    const markup = getComponentMarkup<FilterDrawerModel>(FilterDrawer, data);
    const main = document.querySelector<HTMLElement>('main');

    main?.insertAdjacentHTML('beforeend', markup);

    this.drawerRendered = true;
  }

  renderFiltersBarItems(filters: ResearchSearchApiFilterData[] | undefined) {
    if (!filters) return;

    const filtersBar = this.contentElement?.querySelector<HTMLElement>('.kf-m-filters-bar');

    const data = this.flatFilters(filters);

    filtersBar?.dispatchEvent(
      new CustomEvent('filter-bar-change', {
        detail: {
          data,
        },
      })
    );
  }

  flatFilters(filters: ResearchSearchApiFilterData[]) {
    const criteria: {
      text?: string;
      value?: number;
    }[] = [];

    filters.forEach((filter) => {
      filter.criteria?.forEach((criterion) => {
        criteria.push(criterion);
      });
    });

    return criteria;
  }

  transformFilterDrawerData({
    filters,
    heading,
    subHeading,
    filtersUpdateButtonLabel,
    filtersCloseButtonLabel,
  }: RenderFilterDrawerData): FilterDrawerModel {
    return {
      ...researchSearchFilterDrawerData,
      styleModifier: ['search-page'],
      heading,
      subHeading,
      searchId: this.searchId,
      mainButton: { ...researchSearchFilterDrawerData.mainButton, text: filtersUpdateButtonLabel },
      subButton: { ...researchSearchFilterDrawerData.subButton, text: filtersCloseButtonLabel },
      cssPath: `b-filter-drawer.css?v=${this.timestamp}`,
      ...(filters && {
        criteria: {
          cssPath: `b-accordion.css?v=${this.timestamp}`,
          theme: getCurrentTheme(),
          excludeCss: true,
          ...researchSearchFilterDrawerData.criteria,
          items: filters.map((filter) => ({
            title: filter.name,
            excludeCss: true,
            icon: {
              iconPath: '#kf-chevron-up',
              viewBoxHeight: 24,
              viewBoxWidth: 24,
            },
            filterCriterion: {
              searchId: this.searchId,
              excludeJs: true,
              checkIcon: {
                iconPath: '#kf-check',
                viewBoxWidth: 19,
                viewBoxHeight: 19,
              },
              options: filter?.criteria?.map((criterion) => ({
                text: criterion.text,
                value: criterion.value?.toString(),
              })),
            },
          })),
        },
      }),
    };
  }

  dispatchEventRenderData(items: ResearchSearchApiDataWithStyles[], section?: HTMLElement | null, itemType?: string) {
    if (!section) return;

    if (items?.length) {
      section.dataset.show = 'true';
    } else {
      section.dataset.show = undefined;
    }

    if (itemType === 'insights') {
      section.querySelector<HTMLElement>('.kf-o-insights')?.dispatchEvent(new CustomEvent('render-research-search-data', { detail: items }));
    }
  }

  getPageSize(page?: string) {
    let pageSize: number;
    let lazyLoadMorePageSize: number;
    const numberOfFirstResultItems = +(this.contentElement?.dataset.numberOfFirstResultItems || '8');
    const pageSizeDesktop = +(this.element.dataset.pageSizeDesktop || searchPageSize.desktop);
    const pageSizeTablet = +(this.element.dataset.pageSizeTablet || searchPageSize.tablet);
    const pageSizeMobile = +(this.element.dataset.pageSizeMobile || searchPageSize.mobile);

    if (this.usePagination) return this.getPageSizeForPagination(pageSizeDesktop, pageSizeTablet, pageSizeMobile, page);

    switch (currentViewport()) {
      case 'desktop':
        pageSize = !this.enableExtendedResultsView ? numberOfFirstResultItems : pageSizeDesktop + numberOfFirstResultItems;
        lazyLoadMorePageSize = pageSizeDesktop;
        break;
      case 'tablet':
        pageSize = !this.enableExtendedResultsView ? numberOfFirstResultItems : pageSizeTablet + numberOfFirstResultItems;
        lazyLoadMorePageSize = pageSizeTablet;
        break;
      default:
        pageSize = !this.enableExtendedResultsView ? numberOfFirstResultItems : pageSizeMobile + numberOfFirstResultItems;
        lazyLoadMorePageSize = pageSizeMobile;
        break;
    }

    if (page && page !== '1') {
      pageSize += lazyLoadMorePageSize * (+page - 1);
    }

    return { pageSize, pageSizeDesktop, pageSizeTablet, pageSizeMobile };
  }

  appendQueryToUrl(item: ResearchSearchApiDataWithStyles, query?: string | null) {
    if (!query) return item;

    return {
      ...item,
      articleUrl: queryString.stringifyUrl(
        {
          url: item.articleUrl,
          query: { _fquery: query },
        },
        { skipNull: true, skipEmptyString: true }
      ),
    };
  }

  createPaginationHtml(data: PaginationWithNavStaticHtmlModel) {
    return getComponentMarkup(PaginationWithNavStaticHtml, { ...data, styleModifier: ['search-page', 'static-html'] });
  }

  refreshPagination(newData?: PaginationWithNavStaticHtmlModel) {
    const paginationContainer = this.getCurrentPagination();

    if (!paginationContainer) return;

    if (!newData) {
      paginationContainer.innerHTML = '';

      return;
    }

    const html = this.createPaginationHtml(newData);

    paginationContainer.innerHTML = html;
    this.addClickEventHandlerToPaginationNodes();
  }

  getCurrentPagination() {
    const paginationContainerSelector = '[data-is-pagination-container]';

    return this.contentElement?.querySelector(paginationContainerSelector);
  }

  calculateSearchApiParameters(page?: number) {
    const { numberOfFirstResultItems, pageSize } = this.layoutParameters;
    let skip = undefined;
    let take = 0;

    if (!page || page === 1) {
      take = this.enableExtendedResultsView ? numberOfFirstResultItems + pageSize : numberOfFirstResultItems;
    } else {
      take = this.usePagination ? pageSize : numberOfFirstResultItems + pageSize * (page + (this.enableExtendedResultsView ? 1 : 0));
      skip = !this.usePagination ? 0 : numberOfFirstResultItems + pageSize * (page - (this.enableExtendedResultsView ? 1 : 2));
    }

    return { skip, take };
  }

  createPaginationDataFromSearchApiResponse(response: ResearchSearchApiResponse): PaginationWithNavStaticHtmlModel | undefined {
    const { numberOfFirstResultItems, pageSize } = this.layoutParameters;
    const { offset, total } = response.data.meta;
    const count = response.data.items.length;

    if (count === 0) return undefined;

    const url = new URL(window.location.href);
    const query = url.searchParams.get(queryKey);
    const filters = url.searchParams.get(searchFilterKey);
    const sizeOfFirstPage = numberOfFirstResultItems + (this.enableExtendedResultsView ? pageSize : 0);

    if (total <= sizeOfFirstPage) return undefined;

    const numberOfPages = Math.ceil((total - sizeOfFirstPage) / pageSize) + 1;
    const currentPage = offset + 1 <= sizeOfFirstPage ? 1 : Math.ceil((offset + 1 - sizeOfFirstPage) / pageSize) + 1;
    const pageOfLastResult = offset + count <= sizeOfFirstPage ? 1 : Math.ceil((offset + count - sizeOfFirstPage) / pageSize) + 1;

    if (currentPage !== pageOfLastResult || offset >= total) {
      console.log('Response data is not valid');

      return undefined;
    }

    const createQueryString = (_page: number) => {
      const pageUrl = new URL(window.location.pathname, window.location.origin);

      if (query) pageUrl.searchParams.set('query', query.toString());
      if (filters) pageUrl.searchParams.set('categories', filters);
      if (_page && _page !== 1) pageUrl.searchParams.set('page', _page.toString());
      this.syncWorldwideScopeInSearchUrl(pageUrl);

      return '?' + pageUrl.searchParams.toString();
    };

    const disablePrevNav = currentPage === 1 ? true : undefined;
    const disableNextNav = currentPage === numberOfPages ? true : undefined;

    const createPages = () => {
      let pages: number[] = [];
      const handleIfNumberOfPagesLessThanOrEqualTo6 = () => {
        if (numberOfPages <= 6)
          for (let i = 1; i <= numberOfPages; i++) {
            pages.push(i);
          }
      };

      handleIfNumberOfPagesLessThanOrEqualTo6();

      const handleRemainingCases = () => {
        if (numberOfPages <= 6) return;

        // Case 1: Current page near start
        if (currentPage <= 4) {
          pages = [1, 2, 3, 4, 5, numberOfPages];
        }
        // Case 2: Current page near end
        else if (currentPage >= numberOfPages - 2) {
          pages.push(1);
          for (let i = 1; i <= 5; i++) {
            pages.push(numberOfPages - 5 + i);
          }
        }
        // Case 3: Current page in middle
        else {
          pages.push(1);
          for (let i = -2; i <= 1; i++) {
            pages.push(currentPage + i);
          }
          pages.push(numberOfPages);
        }
      };

      handleRemainingCases();

      return pages;
    };

    return {
      numberOfPages,
      currentPage,
      disablePrevNav,
      disableNextNav,
      prevNavUrl: disablePrevNav ? undefined : createQueryString(currentPage - 1),
      nextNavUrl: disableNextNav ? undefined : createQueryString(currentPage + 1),
      prevNavAriaLabel: this.paginationPrevNavAriaLabel,
      nextNavAriaLabel: this.paginationNextNavAriaLabel,
      nodeAriaLabel: this.paginationNodeAriaLabel,
      nodes: createPages().map((page) => ({
        pageNumber: page,
        url: createQueryString(page),
      })),
    };
  }

  get layoutParameters() {
    const numberOfFirstResultItems = +(this.contentElement?.dataset.numberOfFirstResultItems || '8');

    if (!this.enableExtendedResultsView) return { numberOfFirstResultItems, pageSize: numberOfFirstResultItems };

    const pageSizeDesktop = +(this.element.dataset.pageSizeDesktop || searchPageSize.desktop);
    const pageSizeTablet = +(this.element.dataset.pageSizeTablet || searchPageSize.tablet);
    const pageSizeMobile = +(this.element.dataset.pageSizeMobile || searchPageSize.mobile);

    let pageSize;

    switch (currentViewport()) {
      case 'desktop':
        pageSize = pageSizeDesktop;
        break;
      case 'tablet':
        pageSize = pageSizeTablet;
        break;
      default:
        pageSize = pageSizeMobile;
        break;
    }

    return { pageSize, numberOfFirstResultItems };
  }

  getPageSizeForPagination(pageSizeDesktop: number, pageSizeTablet: number, pageSizeMobile: number, page?: string) {
    let pageSize: number;

    switch (currentViewport()) {
      case 'desktop':
        pageSize = pageSizeDesktop;
        break;
      case 'tablet':
        pageSize = pageSizeTablet;
        break;
      default:
        pageSize = pageSizeMobile;
        break;
    }

    if (page && page === '1' && this.enableExtendedResultsView) pageSize += +(this.contentElement?.dataset.numberOfFirstResultItems || '8');

    return { pageSize, pageSizeDesktop, pageSizeTablet, pageSizeMobile };
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const element = document.querySelector<HTMLFormElement>(getClass());

  if (!element) return;

  new ResearchSearchBar(element).search();
});

export {};
